import ArrowUpRight16 from '@carbon/icons-react/es/arrow--up-right/16';
import Checkmark16 from '@carbon/icons-react/es/checkmark/16';
import ChevronRight16 from '@carbon/icons-react/es/chevron--right/16';
import Copy16 from '@carbon/icons-react/es/copy/16';
import { Skeleton } from 'antd';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import useCopyClipboard from 'react-use-clipboard';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { ProjectGlobalConversation } from 'api/project-conversations';
import { Button } from 'components/atoms/button';
import { RTE } from 'components/atoms/rte';
import { toastService } from 'components/molecules/toast';
import { useTaskDrawer } from 'components/organisms/task-drawer';
import { useTranslation } from 'hooks/use-translation';
import { unreachable } from 'utils/unreachable';
import {
  getProjectStatusUpdatesDetailUrl,
  getSpaceTabDetailUrl,
} from 'utils/urls';
import { Body, ChannelDetailsWrapper, Heading, Links } from './components';
import { useFetchDescription } from './util';

interface Props {
  projectId: ProjectId;
  activeConversation: ProjectGlobalConversation;
}

const BodyContainer = styled.div`
  position: relative;
  margin-bottom: var(--spacing-05);
  width: 75%;
  border: 1px solid var(--gray-10);
  border-radius: 16px;
  ${Body} {
    margin-bottom: 0;
    border-radius: 16px;
  }
  &:hover {
    cursor: pointer;
    ${Body} {
      * {
        background-color: var(--coolest-gray-20) !important;
      }
    }
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(244, 244, 244, 0) 60%,
    rgba(255, 255, 255, 1) 100%
  );
  border-radius: 16px;
`;

const getUrl = (
  projectId: ProjectId,
  activeConversation: ProjectGlobalConversation,
): string | undefined => {
  switch (activeConversation?.displayEntityType) {
    case 'tasks':
      return `${window.location.origin}${window.location.pathname}#task_drawer=${projectId[1]}.${activeConversation.entityId}`;
    case 'space-tabs': {
      return `${window.location.origin}${getSpaceTabDetailUrl([
        'space_tab_id',
        projectId[1],
        activeConversation.spaceTab.space.spaceId,
        activeConversation.spaceTab.spaceTabId,
      ])}`;
    }
    case 'status-updates': {
      return `${window.location.origin}${getProjectStatusUpdatesDetailUrl(
        projectId[1],
        activeConversation.statusUpdate.statusUpdateId,
      )}`;
    }
    default:
      unreachable(activeConversation as never);
  }

  return undefined;
};

export const useOpenConversationItem = (
  projectId: ProjectId,
  activeConversation?: ProjectGlobalConversation,
): (() => void) => {
  const [, show] = useTaskDrawer();

  return useCallback(() => {
    switch (activeConversation?.displayEntityType) {
      case 'tasks':
        show(projectId, ['task_id', activeConversation.entityId]);

        return;
      case 'space-tabs': {
        const url = getSpaceTabDetailUrl([
          'space_tab_id',
          projectId[1],
          activeConversation.spaceTab.space.spaceId,
          activeConversation.spaceTab.spaceTabId,
        ]);

        window.open(url);
        break;
      }
      case 'status-updates': {
        const url = getProjectStatusUpdatesDetailUrl(
          projectId[1],
          activeConversation.statusUpdate.statusUpdateId,
        );

        window.open(url);
        break;
      }
      default:
        unreachable(activeConversation as never);
    }
  }, [activeConversation, projectId, show]);
};

const Description: FC<Props & { onOpen: () => void }> = ({
  activeConversation,
  projectId,
  onOpen,
}) => {
  const { data, isFetching } = useFetchDescription(
    projectId[1],
    activeConversation,
  );
  const { createdAt, description } = data || {};

  if (isFetching) {
    return (
      <Body>
        <Skeleton paragraph={{ rows: 2 }} />
      </Body>
    );
  }
  if (activeConversation.displayEntityType === 'tasks' && description) {
    return (
      <BodyContainer onClick={onOpen}>
        <Body isRTE={activeConversation.displayEntityType === 'tasks'}>
          <RTE
            data={(description as string) || ''}
            projectId={projectId}
            readonly
          />
        </Body>
        <Overlay />
      </BodyContainer>
    );
  }
  if (activeConversation.displayEntityType === 'tasks' && !description) {
    return <Body>{createdAt}</Body>;
  }

  return <Body>{createdAt}</Body>;
};

export const GlobalConversationDetails: FC<Props> = ({
  projectId,
  activeConversation,
}) => {
  const onOpen = useOpenConversationItem(projectId, activeConversation);
  const [copied, copyToClipboard] = useCopyClipboard(
    getUrl(projectId, activeConversation) || '',
    {
      successDuration: 5 * 1000,
    },
  );
  const successTitle = useTranslation('toolbar.info_panel_success');
  const successBody = useTranslation(
    `projects.conversations.${activeConversation.displayEntityType}.copied_body`,
  );

  return (
    <ChannelDetailsWrapper>
      <Heading>{activeConversation.entityName}</Heading>
      <Description
        activeConversation={activeConversation}
        projectId={projectId}
        onOpen={onOpen}
      />
      <Links>
        <Button
          size="small"
          type="link"
          iconPlacement="right"
          icon={copied ? <Checkmark16 /> : <Copy16 />}
          onClick={() => {
            copyToClipboard();
            toastService({
              title: successTitle,
              message: successBody,
            }).success();
          }}
        >
          <FormattedMessage id="copy_link" />
        </Button>
        <Button
          size="small"
          type="link"
          iconPlacement="right"
          icon={
            activeConversation.displayEntityType === 'tasks' ? (
              <ChevronRight16 />
            ) : (
              <ArrowUpRight16 />
            )
          }
          onClick={onOpen}
        >
          <FormattedMessage
            id={`${activeConversation.displayEntityType}.open`}
          />
        </Button>
      </Links>
    </ChannelDetailsWrapper>
  );
};
